import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';

import { ApiError } from '@/api/client';
import { listLocations } from '@/api/locations';
import {
  emptyRules,
  getCategoryAllowedLocations,
  getCategoryRules,
  setCategoryAllowedLocations,
  setCategoryRules,
  type AllowedLocationEntry,
  type Rules,
  type TeamCategory,
} from '@/api/teamCategories';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ConfirmDialog';
import { FormErrorCallout } from '@/components/FormErrorCallout';
import { RulesForm } from '@/components/RulesForm';
import { activePresetId, hasAnyValueSet, RULE_PRESETS } from '@/lib/rulesPresets';
import { validateRulesClient } from '@/lib/validateRulesClient';
import { cn } from '@/lib/utils';

// The "Schemaregler" tab of the team-category side-drawer: per-category
// scheduling rules + allowed-location list. Body extracted from the former
// CategoryRulesModal (dialog chrome dropped — the Sheet supplies it). Radix
// Select portals to the document body by default, which layers correctly
// above the Sheet, so no `container` hack is needed here (that was only
// required inside a native <dialog>.showModal top-layer).

export interface CategoryRulesTabProps {
  slug: string;
  category: TeamCategory;
  open: boolean;
  onClose: () => void;
}

export function CategoryRulesTab({ slug, category, open, onClose }: CategoryRulesTabProps) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const [rules, setRules] = useState<Rules>(emptyRules);
  const [selectedLocationIDs, setSelectedLocationIDs] = useState<Set<string>>(new Set());
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [clientIssues, setClientIssues] = useState<string[]>([]);
  const [errorTick, setErrorTick] = useState(0);

  const rulesQuery = useQuery({
    queryKey: ['team-category-rules', slug, category.id],
    queryFn: () => getCategoryRules(token, slug, category.id),
    enabled: !!token && !!slug && open,
  });
  const locationsAllowQuery = useQuery({
    queryKey: ['team-category-allowed-locations', slug, category.id],
    queryFn: () => getCategoryAllowedLocations(token, slug, category.id),
    enabled: !!token && !!slug && open,
  });
  const locationsQuery = useQuery({
    queryKey: ['locations', slug],
    queryFn: () => listLocations(token, slug),
    enabled: !!token && !!slug && open,
  });

  // Seed once per open; ref-guard so query refetches don't clobber edits.
  const seededRef = useRef(false);
  useEffect(() => {
    if (!open) {
      seededRef.current = false;
      return;
    }
    if (seededRef.current) return;
    if (!rulesQuery.data || !locationsAllowQuery.data) return;
    setRules(rulesQuery.data);
    setSelectedLocationIDs(new Set(locationsAllowQuery.data.entries.map((e) => e.location_id)));
    seededRef.current = true;
  }, [open, rulesQuery.data, locationsAllowQuery.data]);

  const mutation = useMutation({
    mutationFn: async () => {
      await setCategoryRules(token, slug, category.id, rules);
      const entries: AllowedLocationEntry[] = Array.from(selectedLocationIDs).map((id, i) => ({
        location_id: id,
        zone_id: null,
        preference_order: i,
      }));
      await setCategoryAllowedLocations(token, slug, category.id, entries);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['team-category-rules', slug, category.id] });
      void queryClient.invalidateQueries({
        queryKey: ['team-category-allowed-locations', slug, category.id],
      });
      onClose();
    },
    onError: (err) => {
      setErrorCode(err instanceof ApiError ? err.code : 'unknown');
      setErrorTick((n) => n + 1);
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setErrorCode(null);
    const issues = validateRulesClient(rules, t);
    setClientIssues(issues);
    if (issues.length > 0) {
      setErrorTick((n) => n + 1);
      return;
    }
    mutation.mutate();
  };

  const onApplyPreset = async (presetRules: Rules) => {
    if (hasAnyValueSet(rules)) {
      const ok = await confirm({
        title: t('rules.presets.confirmTitle'),
        body: t('rules.presets.confirmBody'),
        confirmLabel: t('rules.presets.confirmCta'),
      });
      if (!ok) return;
    }
    setRules(presetRules);
    if (clientIssues.length > 0) setClientIssues([]);
    if (errorCode) setErrorCode(null);
  };

  const isLoading =
    rulesQuery.isPending || locationsAllowQuery.isPending || locationsQuery.isPending;
  const activePreset = activePresetId(rules);

  return (
    <form onSubmit={onSubmit} noValidate className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 sm:p-6">
        {(clientIssues.length > 0 || errorCode) && (
          <FormErrorCallout key={errorTick} heading={t('settings.teamCategories.rules.error.heading')}>
            {clientIssues.length > 0 ? (
              <>
                <p>{t('settings.teamCategories.rules.error.fixThese')}</p>
                <ul className="list-disc space-y-0.5 pl-5">
                  {clientIssues.map((issue, i) => (
                    <li key={i}>{issue}</li>
                  ))}
                </ul>
              </>
            ) : (
              <p>
                {t(`settings.teamCategories.rules.error.${errorCode}`, {
                  defaultValue: t('settings.teamCategories.rules.error.unknown'),
                })}
              </p>
            )}
          </FormErrorCallout>
        )}

        {!isLoading && (
          <section className="flex flex-col gap-2 rounded-xl border border-border bg-accent-soft/60 p-3">
            <div className="flex flex-col gap-0.5">
              <span className="font-heading text-sm font-semibold text-foreground">
                {t('rules.presets.heading')}
              </span>
              <span className="text-xs text-muted-foreground">{t('rules.presets.help')}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {RULE_PRESETS.map((p) => {
                const active = activePreset === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    aria-pressed={active}
                    onClick={() => {
                      void onApplyPreset(p.rules);
                    }}
                    disabled={mutation.isPending}
                    className={cn(
                      'rounded-pill border px-3 py-1 text-xs font-medium transition-colors',
                      active
                        ? 'border-lime bg-lime text-ink'
                        : 'border-border bg-background text-foreground hover:border-lime hover:bg-lime hover:text-ink',
                      mutation.isPending && 'cursor-not-allowed opacity-50',
                    )}
                  >
                    {t(`rules.presets.${p.id}`)}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {isLoading ? (
          <p className="text-sm text-muted-foreground">{t('settings.teamCategories.rules.loading')}</p>
        ) : (
          <RulesForm
            rules={rules}
            onChange={(next) => {
              setRules(next);
              if (clientIssues.length > 0) setClientIssues([]);
              if (errorCode) setErrorCode(null);
            }}
            locations={locationsQuery.data?.locations ?? []}
            selectedLocationIDs={selectedLocationIDs}
            onSelectedLocationsChange={setSelectedLocationIDs}
            disabled={mutation.isPending}
          />
        )}
      </div>

      <footer className="flex justify-end gap-2 border-t border-border bg-background p-4">
        <Button type="button" variant="outline" onClick={onClose} disabled={mutation.isPending}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={mutation.isPending || isLoading}>
          {mutation.isPending
            ? t('settings.teamCategories.rules.saving')
            : t('settings.teamCategories.rules.save')}
        </Button>
      </footer>
    </form>
  );
}
